import { Button, Form, Input, Select } from "antd";
import { useUserStore } from "../../../stores/userStore";
import { Country } from "country-state-city";

export const DELIVERY_MESSAGE_OPTIONS = [
  { value: "부재시 경비실에 맡겨주세요", label: "부재시 경비실에 맡겨주세요" },
  { value: "문 앞에 놓아주세요", label: "문 앞에 놓아주세요" },
  { value: "부재시 연락주세요", label: "부재시 연락주세요" },
  { value: "배송 전 연락 부탁드립니다", label: "배송 전 연락 부탁드립니다" },
  { value: "custom", label: "직접 입력" },
];

export const CampaignShipmentForm = ({
  path,
  showFillExistingButton = true,
  noWrapper = false,
}) => {
  const { userInfo } = useUserStore();
  const formInstance = Form.useFormInstance();

  const name = (field) => (path ? [...path, field] : field);

  const deliveryMessageType = Form.useWatch(
    name("delivery_message_type"),
    formInstance
  );

  const fillExistingAddress = () => {
    if (!userInfo?.user_info) return;

    const receiverName =
      userInfo.user_info.first_name && userInfo.user_info.last_name
        ? `${userInfo.user_info.last_name} ${userInfo.user_info.first_name}`
        : "";

    formInstance.setFieldsValue({
      receiver_name: receiverName,
      receiver_phone: userInfo.user_info.phone || "",
      country: userInfo.user_info.country || "",
      state_province: userInfo.user_info.state_province || "",
      city: userInfo.user_info.city || "",
      postal_code: userInfo.user_info.postal_code || "",
      street_address: userInfo.user_info.street_address || "",
      address_line: userInfo.user_info.address_line || "",
    });
  };

  const handleDeliveryMessageChange = (value) => {
    const deliveryValue = value !== "custom" ? value : "";
    if (!path) {
      formInstance.setFieldsValue({ delivery_message: deliveryValue });
    } else {
      const [parentKey, creatorId] = path;
      formInstance.setFieldsValue({
        [parentKey]: {
          ...formInstance.getFieldValue(parentKey),
          [creatorId]: {
            ...formInstance.getFieldValue([parentKey, creatorId]),
            delivery_message: deliveryValue,
          },
        },
      });
    }
  };

  const fields = (
    <>
      <Form.Item
        name={name("receiver_name")}
        label="수령인 이름"
        rules={[{ required: true, message: "수령인 이름을 입력해주세요" }]}
      >
        <Input placeholder="수령인 이름을 입력해주세요" />
      </Form.Item>

      <Form.Item
        name={name("receiver_phone")}
        label="수령인 전화번호"
        rules={[{ required: true, message: "수령인 전화번호를 입력해주세요" }]}
      >
        <Input placeholder="수령인 전화번호를 입력해주세요 (예: 010-1234-5678)" />
      </Form.Item>

      <Form.Item
        name={name("country")}
        label="국가"
        rules={[{ required: true, message: "국가를 선택해주세요" }]}
      >
        <Select
          placeholder="국가를 선택해주세요"
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        >
          {Country.getAllCountries().map((country) => (
            <Select.Option
              key={country.isoCode}
              value={country.isoCode}
              label={`${country.name} (${country.isoCode})`}
            >
              {country.name} ({country.isoCode})
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name={name("state_province")}
        label="시/도"
        rules={[{ required: true, message: "시/도를 입력해주세요" }]}
      >
        <Input placeholder="시/도를 입력해주세요 (예: 서울특별시, 경기도)" />
      </Form.Item>

      <Form.Item
        name={name("city")}
        label="시/군/구"
        rules={[{ required: true, message: "시/군/구를 입력해주세요" }]}
      >
        <Input placeholder="시/군/구를 입력해주세요 (예: 마포구, 강남구)" />
      </Form.Item>

      <Form.Item
        name={name("postal_code")}
        label="우편번호"
        rules={[{ required: true, message: "우편번호를 입력해주세요" }]}
      >
        <Input placeholder="우편번호를 입력해주세요" />
      </Form.Item>

      <Form.Item
        name={name("street_address")}
        label="도로명 주소"
        rules={[{ required: true, message: "도로명 주소를 입력해주세요" }]}
      >
        <Input placeholder="도로명 주소를 입력해주세요 (예: 마포대로 123)" />
      </Form.Item>

      <Form.Item
        name={name("address_line")}
        label="상세주소"
        rules={[{ required: true, message: "상세주소를 입력해주세요" }]}
      >
        <Input placeholder="상세주소를 입력해주세요 (예: 래미안아파트 101동 1504호)" />
      </Form.Item>

      <Form.Item name={name("customs_clearance_number")} label="통관번호">
        <Input placeholder="통관번호를 입력해주세요" />
      </Form.Item>

      <Form.Item name={name("delivery_message_type")} label="배송 메시지">
        <Select
          placeholder="배송 메시지를 선택해주세요"
          onChange={handleDeliveryMessageChange}
        >
          {DELIVERY_MESSAGE_OPTIONS.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {deliveryMessageType === "custom" && (
        <Form.Item name={name("delivery_message")}>
          <Input.TextArea
            rows={3}
            placeholder="여기에 입력해주세요."
            style={{ resize: "none" }}
          />
        </Form.Item>
      )}
    </>
  );

  if (noWrapper) {
    return <div className="space-y-4">{fields}</div>;
  }

  return (
    <div className="mb-3 border border-[#e0e0e0] rounded-lg px-[14px] py-[10px] bg-[#f8f9fa]">
      <div className="flex justify-between items-center mb-[10px]">
        <div className="text-[#424242] text-[15px] font-bold text-center">
          배송 정보
        </div>
        {showFillExistingButton && (
          <Button
            size="small"
            onClick={fillExistingAddress}
            className="bg-white border-[#d9d9d9] text-[#424242]"
          >
            기본 배송지
          </Button>
        )}
      </div>
      {fields}
    </div>
  );
};
